import { useCallback, useMemo } from 'react';
import { useGetApplicationCounterQuery } from '../../../../api/queries/getApplicationCounter';
import { useGetDbsInstantApprovalRateQuery } from '../../../../api/queries/getDbsInstantApprovalRate';
import { ModuleRole, ApplicationStage, type StatisticFilterPayload } from '../../../../api/types';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import { useMultipleDealerIds } from '../../../../components/contexts/DealerContextManager';
import { toDuration } from '../../../../utilities/fp';

const convertSecondsToDuration = (seconds: number) => toDuration(seconds) || '-';

type MonthData = {
    submitted: number;
    approved: number;
    formattedSubmittedTurnAround: string;
    formattedCustomerInfoTurnAround: string;
    dbsInstantApprovalStats: {
        total: number;
        instantApproved: number;
        rate: number;
    };
};

export type ApplicationCounter = {
    currentMonthData: MonthData;
    lastMonthData: MonthData;
};

const useApplicationCounterData = () => {
    // prepare filter
    const company = useCompany(true);
    const { dealerIds } = useMultipleDealerIds();
    const filter: StatisticFilterPayload = useMemo(
        () => ({
            companyId: company?.id,
            dealerIds,
            moduleRole: ModuleRole.Application,
            stage: ApplicationStage.Financing,
        }),
        [company, dealerIds]
    );

    // fetch data
    const {
        data,
        loading: isLoading,
        error,
    } = useGetApplicationCounterQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter,
        },
        skip: !company?.id,
    });

    const {
        data: dbsInstantApprovalData,
        loading: isDbsInstantApprovalDataLoading,
        error: getDbsInstantApprovalError,
    } = useGetDbsInstantApprovalRateQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                companyId: company?.id,
            },
        },
        skip: !company?.id || !company?.isInstantApprovalStatsEnabled,
    });

    const generateData = useCallback(
        async (applyData: (data) => void) => {
            if (!data?.metrics) {
                applyData(null);

                return;
            }
            const formattedData: ApplicationCounter = {
                currentMonthData: {
                    approved: data.metrics.currentMonth?.approved,
                    submitted: data.metrics.currentMonth?.submitted,
                    formattedSubmittedTurnAround: convertSecondsToDuration(
                        data.metrics.currentMonth?.submittedTurnAround
                    ),
                    formattedCustomerInfoTurnAround: convertSecondsToDuration(
                        data.metrics.currentMonth?.customerInfoTurnAround
                    ),
                    dbsInstantApprovalStats: {
                        total: dbsInstantApprovalData?.metrics?.currentMonth?.dbsTotalApplicationCount,
                        instantApproved: dbsInstantApprovalData?.metrics?.currentMonth?.dbsInstantApprovalCount,
                        rate: dbsInstantApprovalData?.metrics?.currentMonth?.dbsInstantApprovalRate,
                    },
                },
                lastMonthData: {
                    approved: data.metrics.lastMonth?.approved,
                    submitted: data.metrics.lastMonth?.submitted,
                    formattedSubmittedTurnAround: convertSecondsToDuration(data.metrics.lastMonth?.submittedTurnAround),
                    formattedCustomerInfoTurnAround: convertSecondsToDuration(
                        data.metrics.lastMonth?.customerInfoTurnAround
                    ),
                    dbsInstantApprovalStats: {
                        total: dbsInstantApprovalData?.metrics?.lastMonth?.dbsTotalApplicationCount,
                        instantApproved: dbsInstantApprovalData?.metrics?.lastMonth?.dbsInstantApprovalCount,
                        rate: dbsInstantApprovalData?.metrics?.lastMonth?.dbsInstantApprovalRate,
                    },
                },
            };
            applyData(formattedData);
        },
        [data, dbsInstantApprovalData]
    );

    return useMemo(
        () => ({
            isLoading: isLoading || isDbsInstantApprovalDataLoading,
            error: error || getDbsInstantApprovalError,
            generateData,
        }),
        [isLoading, isDbsInstantApprovalDataLoading, error, getDbsInstantApprovalError, generateData]
    );
};

export default useApplicationCounterData;
